import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useFormikContext } from 'formik';
import {
  type ChangeEvent,
  type FC,
  type MutableRefObject,
  type ReactElement,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { SlabContext } from '@core/context/SlabContext';
import {
  EmptyFormikQuote,
  type QuoteFormikType,
  type QuoteSubmitType,
  type RequiredQuoteFieldsInfo,
} from '@core/formik/quote';
import { useQuoteDrawer } from '@core/hooks/useQuoteDrawer';
import { useQuoteStatusOptions } from '@core/hooks/useQuoteStatusOptions';
import Enums from '@core/types/api/Enums';
import { NewProject, type Project } from '@core/types/api/Project/Project';
import type { QuoteConfig } from '@core/types/api/QuoteConfig/QuoteConfig';
import type { User } from '@core/types/api/User/User';
import type { List } from '@core/types/list';
import { hasCompetitorLossDataFromFormik } from '@core/utils/competitors';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { getCurrencySymbol } from '@core/utils/format';
import { SetFormikValue } from '@core/utils/formikHelpers';
import {
  defaultQuoteExpiration,
  getIsShowCompetitorInfoSectionFromQuoteFormik,
} from '@core/utils/quoteHelpers';
import { requiredLabel } from '@core/utils/strings';
import { NIL_UUID } from '@core/utils/uuid';
import { AddressField } from '@web/components/Address/AddressField';
import { Input } from '@web/components/Input/Input';
import { InputDropdown, type InputOption } from '@web/components/InputDropdown/InputDropdown';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { ApiLookupInput } from '@web/components/LookupInput/ApiLookupInput';
import { LocalLookupInput } from '@web/components/LookupInput/LocalLookupInput';
import type { LookupInputOption } from '@web/components/LookupInput/LookupInputSharedComponents';
import { LossReasonSection } from '@web/components/LossReasonSection/LossReasonSection';
import { useSalespersonList } from '@web/hooks/useSalespersonList';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { useTenantMargins } from '@web/hooks/useTenantMargins';
import { isValidReference, lookups } from '@web/utils/DomainHelpers';
import { getTenantRequiresApprovals } from '@web/utils/TenantConfig';

import { QuoteDrawerMode } from '../QuoteDrawer';
import type { QuickProjectMode, QuoteDrawerFormikType } from '../utils';
import { InlineCompanyCreate } from './InlineCompanyCreate';
import { NotesSection } from './NotesSection';
import type { ProductsSectionProps } from './ProductsSection';
import type { ProductRowsOverride } from './useBuyerPricingReconciler';

/**
 * Given a project, return lookups for all quote configs that exist for any plant of the selected project.
 */
const quoteConfigLookups = ({
  quoteConfigList,
  project,
}: {
  quoteConfigList: List<QuoteConfig>;
  project: Project | undefined;
}): InputOption[] => {
  if (project === undefined) {
    return [];
  }

  // If project has no plants, allow all quote configs
  if (project.plants.length === 0) {
    return lookups({
      items: quoteConfigList.items,
      label: (qc) => qc.name,
      // TODO: #3544 will refactor this. Choosing 0 more items for now.
      count: 0,
    });
  }

  return lookups({
    items: quoteConfigList.items.filter(
      (qc) =>
        // If quote config has no plants, allow it
        qc.plants.length === 0 ||
        // Otherwise, check for matching plants
        project.plants.some((pp) => qc.plants.some((qcPlant) => qcPlant.id === pp.plant.id)),
    ),
    label: (qc) => qc.name,
    // TODO: #3544 will refactor this. Choosing 0 more items for now.
    count: 0,
  });
};

type RequiredFieldsProp = {
  requiredFields: RequiredQuoteFieldsInfo;
};

type ReconcileBuyerPricing = (
  newCompanyID: string | null,
  productsOverride?: ProductRowsOverride,
) => Promise<void> | void;

type QuoteInformationSectionProps = ProductsSectionProps &
  RequiredFieldsProp & {
    mode: QuoteDrawerMode;
    submitType: QuoteSubmitType;
    reconcileBuyerPricing: ReconcileBuyerPricing;
    openedFromProject?: boolean;
    hasSeededFromProjectRef: MutableRefObject<boolean>;
    pendingProjectChangeRef: MutableRefObject<boolean>;
  };

const QuickModeFields: FC<
  RequiredFieldsProp & { reconcileBuyerPricing: ReconcileBuyerPricing }
> = ({ requiredFields, reconcileBuyerPricing }) => {
  const formikBag = useFormikContext<QuoteDrawerFormikType>();
  const projectMode = formikBag.values.quickProjectMode ?? 'new';

  // formikBag from useFormikContext is a new object every render, so useCallback
  // with [formikBag] as a dep would re-create the callback on every render anyway.
  // Removing the wrapper entirely is equivalent and avoids the misleading memoization.
  const handleProjectModeChange = (_: ChangeEvent<HTMLInputElement>, value: string): void => {
    const newMode = value as QuickProjectMode;
    SetFormikValue(formikBag, 'quickProjectMode', newMode);
    if (newMode === 'new') {
      SetFormikValue(formikBag, 'project', { id: null, option: null });
      formikBag.setFieldTouched('project', false, false);
    }
  };

  return (
    <>
      <Box>
        <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
          Buyer Information{requiredFields.company ? '*' : ''}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <InlineCompanyCreate
            showRadioButtons
            onCompanyCreated={(company, contact) => {
              SetFormikValue(formikBag, 'company', company);
              SetFormikValue(
                formikBag,
                'contacts',
                hasValue(contact) ? [contact] : EmptyFormikQuote.contacts,
              );
              reconcileBuyerPricing(company.id);
            }}
          >
            <ApiLookupInput
              formState={formikBag.values}
              name='company'
              label={requiredLabel('Company', requiredFields.company)}
              route={{
                barrel: 'GET companies',
                args: (inputText, { constructQueryParams }) =>
                  constructQueryParams(inputText, {
                    filterBy: [
                      {
                        name: 'isArchived',
                        operation: Enums.FilterOperation.Equals,
                        values: [false],
                      },
                    ],
                  }),
                transform: (companyList): LookupInputOption[] =>
                  lookups({
                    ...companyList,
                    label: (c) => c.name,
                  }),
              }}
              onMatchChange={(newCompanyID): void => {
                SetFormikValue(formikBag, 'contacts', EmptyFormikQuote.contacts);
                reconcileBuyerPricing(newCompanyID);
              }}
            />
          </InlineCompanyCreate>
        </Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
          Project
        </Typography>
        <RadioGroup row value={projectMode} onChange={handleProjectModeChange}>
          <FormControlLabel
            value='new'
            control={<Radio size='small' />}
            label='Create new project'
          />
          <FormControlLabel
            value='existing'
            control={<Radio size='small' />}
            label='Add to existing project'
          />
        </RadioGroup>
        {projectMode === 'existing' && (
          <Box sx={{ mt: 1 }}>
            <ApiLookupInput
              formState={formikBag.values}
              name='project'
              label='Project*'
              route={{
                barrel: 'GET project summaries',
                args: (inputText, { constructQueryParams }) =>
                  constructQueryParams(inputText, {
                    filterBy: [
                      {
                        name: 'isArchived',
                        operation: Enums.FilterOperation.Equals,
                        values: [false],
                      },
                    ],
                  }),
                transform: (pl): LookupInputOption[] =>
                  lookups({
                    ...pl,
                    label: (p) => p.name,
                  }),
              }}
            />
          </Box>
        )}
      </Box>
      {projectMode === 'new' && (
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
            Delivery address*
          </Typography>
          <AddressField name='quickAddress' />
        </Box>
      )}
      <NotesSection label='Notes (optional)' />
    </>
  );
};

export const QuoteInformationSection: FC<QuoteInformationSectionProps> = ({
  curProductsRef,
  requiredFields,
  mode,
  submitType,
  reconcileBuyerPricing,
  openedFromProject = false,
  hasSeededFromProjectRef,
  pendingProjectChangeRef,
}: QuoteInformationSectionProps): ReactElement => {
  const ctx = useContext(SlabContext);
  const formikBag = useFormikContext<QuoteFormikType>();
  const isInitialProjectOpen = useRef(
    !hasSeededFromProjectRef.current &&
      openedFromProject &&
      submitType === 'saveAsNewQuote' &&
      mode !== QuoteDrawerMode.Quick &&
      formikBag.values.id === NIL_UUID &&
      formikBag.values.project.id !== null &&
      (formikBag.values.quoteConfig?.id ?? null) === null,
  );
  if (isInitialProjectOpen.current) {
    pendingProjectChangeRef.current = true;
  }
  const quickProjectSelection = useRef<{
    projectID: string | null | undefined;
    expirationDate: string | null;
  }>({ projectID: undefined, expirationDate: null });
  const { defaultIsMoneySwitchOn, marginConfiguration, defaultMargins } = useTenantMargins();
  const defaultToSuggestedPrice = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDefaultPriceToSuggestedPrice,
  ]);
  const hasTierPricing =
    ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    ctx.userInfo.tenant.tierPricing;
  const { onProjectChange, handleProductState } = useQuoteDrawer();

  const companyId = formikBag.values.company?.id ?? null;

  const { data: fixedPricingByCompany } = useSlabQuery(
    'GET tier pricing by company ID',
    { queryParams: { companyIds: [companyId ?? ''] } },
    { enabled: hasTierPricing && hasStringValue(companyId) },
  );

  const {
    isLoading: isLoadingSalespeople,
    isError: isErrorSalespeople,
    data: salespersonList,
  } = useSalespersonList();

  const {
    isLoading: isLoadingProject,
    isError: isErrorProject,
    data: project,
  } = useSlabQuery(
    'GET project by ID',
    { pathParams: { id: formikBag.values.project.id ?? '' } },
    { enabled: formikBag.values.project.id !== null },
  );

  const {
    isLoading: isLoadingQuoteConfigs,
    isError: isErrorQuoteConfigs,
    data: quoteConfigList,
  } = useSlabQuery('GET quote configs', {
    queryParams: {
      page: 0,
      perPage: 0,
      filterBy: [
        { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
        { name: 'plantIsArchived', operation: Enums.FilterOperation.Equals, values: [false] },
      ],
    },
  });

  const {
    isLoading: isLoadingStatuses,
    isError: isErrorStatuses,
    data: statusList,
  } = useSlabQuery('GET quote statuses', {});

  const {
    isLoading: isLoadingQuotePolicies,
    isError: isErrorQuotePolicies,
    data: quotePolicies,
  } = useSlabQuery('GET quote policies', {});

  const {
    isLoading: isLoadingProjectProducts,
    isError: isErrorProjectProducts,
    data: projectProducts,
  } = useSlabQuery(
    'GET project products by project ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    { enabled: formikBag.values.project.id !== null },
  );

  const { data: lossReasons } = useSlabQuery('GET quote loss reasons', {
    queryParams: {
      page: 0,
      perPage: 0,
      filterBy: [{ name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] }],
    },
  });

  const isLoading =
    isLoadingSalespeople || isLoadingQuoteConfigs || isLoadingStatuses || isLoadingQuotePolicies;
  const { getStatusOptions } = useQuoteStatusOptions({
    quoteStatuses: statusList,
    quotePolicies,
  });

  const isDataUndefined =
    salespersonList === undefined ||
    quoteConfigList === undefined ||
    statusList === undefined ||
    quotePolicies === undefined;

  // Handle the automatically set values based on a changing project ID.
  useEffect(() => {
    if (!pendingProjectChangeRef.current) {
      return;
    }
    if (isDataUndefined || isLoadingProject || isLoadingProjectProducts) {
      return;
    }
    pendingProjectChangeRef.current = false;
    const openedFromProjectSeed = isInitialProjectOpen.current;
    isInitialProjectOpen.current = false;
    if (openedFromProjectSeed) {
      hasSeededFromProjectRef.current = true;
    }
    // If the project ID is null, it is always safe to set the form to the cleared values.
    if (formikBag.values.project.id === null && !isLoadingProject && !isLoadingProjectProducts) {
      const {
        newFormikAdditionalProductsByPlants,
        newFormikMainProductsByPlants,
        newFormikTerms,
        newProductState,
        newFormikQuoteConfig,
      } = handleProductState({
        curProductState: curProductsRef.current,
        formikAdditionalProductsByPlants: formikBag.values.additionalProductsByPlants,
        formikMainProductsByPlants: formikBag.values.mainProductsByPlants,
        formikTerms: formikBag.values.terms,
        formikQuoteConfig: formikBag.values.quoteConfig,
        project: undefined,
        quoteConfigList,
        projectID: null,
        isMoneySwitchOn: defaultIsMoneySwitchOn,
        marginConfiguration,
        defaultMargins,
        defaultToSuggestedPrice,
        fixedPricingByCompany,
      });

      SetFormikValue(formikBag, 'quoteConfig', newFormikQuoteConfig);
      SetFormikValue(formikBag, 'mainProductsByPlants', newFormikMainProductsByPlants);
      SetFormikValue(formikBag, 'additionalProductsByPlants', newFormikAdditionalProductsByPlants);
      SetFormikValue(formikBag, 'terms', newFormikTerms);
      SetFormikValue(formikBag, 'user.id', null);

      curProductsRef.current = newProductState;

      const { company, contacts } = onProjectChange(undefined);
      SetFormikValue(formikBag, 'company', company);
      SetFormikValue(formikBag, 'contacts', contacts);

      if (ctx.userInfo.tenant.inheritQuoteExpirationFromProject) {
        SetFormikValue(
          formikBag,
          'expirationDate',
          defaultQuoteExpiration(ctx.userInfo.tenant).toISODate(),
        );
      }
      return;
    }

    // A project has been selected, so once Project and ProjectProducts are loaded, adjust form/state values.
    if (project !== undefined && projectProducts !== undefined) {
      const filledProject = NewProject({
        ...project,
        products: projectProducts,
      });

      const {
        newFormikAdditionalProductsByPlants,
        newFormikMainProductsByPlants,
        newFormikTerms,
        newFormikQuoteConfig,
        newProductState,
      } = handleProductState({
        curProductState: curProductsRef.current,
        formikAdditionalProductsByPlants: formikBag.values.additionalProductsByPlants,
        formikMainProductsByPlants: formikBag.values.mainProductsByPlants,
        formikTerms: formikBag.values.terms,
        formikQuoteConfig: formikBag.values.quoteConfig,
        project: filledProject,
        quoteConfigList,
        projectID: formikBag.values.project.id,
        isMoneySwitchOn: defaultIsMoneySwitchOn,
        marginConfiguration,
        defaultMargins,
        defaultToSuggestedPrice,
        fixedPricingByCompany,
      });

      SetFormikValue(formikBag, 'quoteConfig', newFormikQuoteConfig);
      SetFormikValue(formikBag, 'mainProductsByPlants', newFormikMainProductsByPlants);
      SetFormikValue(formikBag, 'additionalProductsByPlants', newFormikAdditionalProductsByPlants);
      SetFormikValue(formikBag, 'terms', newFormikTerms);
      SetFormikValue(
        formikBag,
        'user.id',
        salespersonList?.items.find((s) => s.id === project?.user.id)?.id ?? '',
      );

      curProductsRef.current = newProductState;

      let buyerID = formikBag.values.company?.id ?? null;
      if (!openedFromProjectSeed) {
        const { company, contacts } = onProjectChange(project);
        SetFormikValue(formikBag, 'company', company);
        SetFormikValue(formikBag, 'contacts', contacts);
        buyerID = company?.id ?? null;
      }

      reconcileBuyerPricing(buyerID, {
        mainProductsByPlants: newFormikMainProductsByPlants,
        additionalProductsByPlants: newFormikAdditionalProductsByPlants,
      });

      if (ctx.userInfo.tenant.inheritQuoteExpirationFromProject) {
        SetFormikValue(
          formikBag,
          'expirationDate',
          defaultQuoteExpiration(ctx.userInfo.tenant, project).toISODate(),
        );
      }
    }
  }, [
    pendingProjectChangeRef.current,
    isDataUndefined,
    isLoadingProject,
    isLoadingProjectProducts,
    project,
    projectProducts,
  ]);

  // Quick mode's project lookup never arms the reconciliation effect above. Capture the
  // expiration when the project selection changes so a manual edit made while an uncached
  // project is loading is not overwritten when the request completes.
  useEffect(() => {
    if (mode !== QuoteDrawerMode.Quick || !ctx.userInfo.tenant.inheritQuoteExpirationFromProject) {
      return;
    }
    const projectID = formikBag.values.project.id;
    if (quickProjectSelection.current.projectID !== projectID) {
      quickProjectSelection.current = {
        projectID,
        expirationDate: formikBag.values.expirationDate,
      };
    }
    if (isLoadingProject) {
      return;
    }
    if (formikBag.values.expirationDate !== quickProjectSelection.current.expirationDate) {
      return;
    }
    SetFormikValue(
      formikBag,
      'expirationDate',
      defaultQuoteExpiration(ctx.userInfo.tenant, project).toISODate(),
    );
  }, [mode, formikBag.values.project.id, isLoadingProject]);

  const currentStatus = statusList?.items.find((s) => s.id === formikBag.values.status.id) ?? null;

  const showCompetitorInfoSection = getIsShowCompetitorInfoSectionFromQuoteFormik(
    statusList?.items,
    formikBag.values,
  );

  // Competitor price inputs use the currency of the quote's first plant.
  const firstPlantID = formikBag.values.mainProductsByPlants?.[0]?.plant?.id ?? null;
  const { data: maybeFirstPlant } = useSlabQuery(
    'GET plant by ID',
    { pathParams: { id: firstPlantID ?? '' } },
    { enabled: firstPlantID !== null },
  );
  const competitorCurrencySymbol = getCurrencySymbol(
    firstPlantID === null ? null : maybeFirstPlant?.currencyCode,
  );
  useEffect(() => {
    if (isLoadingStatuses) return;
    formikBag.setFieldValue('status.systemBehavior', currentStatus?.systemBehavior ?? null);
  }, [formikBag.values.status.id, isLoadingStatuses]);

  const handleStatusChange = (newStatusId: string): void => {
    const newStatus = statusList?.items.find((s) => s.id === newStatusId) ?? null;
    if (newStatus !== null && (newStatus.isLost() || newStatus.isArchived())) {
      return;
    }
    if (!hasCompetitorLossDataFromFormik(formikBag.values)) {
      return;
    }
    formikBag.setFieldValue('lossReason', { id: null, option: null });
    formikBag.setFieldValue('lossReasonNotes', null);
    formikBag.setFieldValue('competitor', { id: null, option: null });
    formikBag.setFieldValue('competitorProducts', []);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const isError =
    isErrorSalespeople ||
    isErrorProject ||
    isErrorProjectProducts ||
    isErrorQuoteConfigs ||
    isErrorQuotePolicies ||
    isErrorStatuses;

  if (isError || isDataUndefined) {
    return <div>ERROR</div>;
  }

  const userLookups = lookups({
    items: salespersonList.items,
    label: (u: User) => u.fullName(),
    count: salespersonList.items.length,
  });

  const initialStatus = statusList.items.find(
    (status) => status.id === formikBag.initialValues.status.id,
  );

  // The create drawer's product requirement is that a new quote is always created in a
  // Draft status; moving to an approval request happens on the follow-up screen. The
  // backend also rejects any non-draft/non-approval status on create, so restricting the
  // dropdown to draft statuses keeps the form's options to creatable values (excluding
  // Sent, Archived, etc.). Editing an existing quote is unaffected, so archiving and
  // other transitions via the dropdown still work.
  const isNewQuote = formikBag.values.id === NIL_UUID;
  const statusOptions = getStatusOptions(isNewQuote ? undefined : initialStatus);

  const quoteConfigDisabled = !isValidReference(formikBag.values.project);
  const quoteConfigLabel = !isValidReference(formikBag.values.project)
    ? 'Select a project'
    : 'Configuration template';

  return (
    <Box display='flex' flexDirection='column' gap='1rem'>
      {mode === QuoteDrawerMode.Quick ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input name='name' label='Name*' />
            </Grid>
            <Grid item xs={6}>
              <Input name='creationDate' label='Creation date' type='date' />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input
                name='expirationDate'
                label={requiredLabel('Expiration date', requiredFields.expirationDate)}
                type='date'
              />
            </Grid>
            <Grid item xs={6}>
              <InputDropdown name='user.id' label='Salesperson*' options={userLookups} />
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input name='name' label='Name*' />
            </Grid>
            <Grid item xs={6}>
              <Input name='revisionNumber' label='Revision' disabled />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input name='creationDate' label='Creation date' type='date' />
            </Grid>
            <Grid item xs={6}>
              <Input name='revisionDate' label='Revision date' type='date' />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input
                name='expirationDate'
                label={requiredLabel('Expiration date', requiredFields.expirationDate)}
                type='date'
              />
            </Grid>
            <Grid item xs={6}>
              <InputDropdown
                name='status.id'
                label='Status*'
                options={statusOptions}
                onSelect={async (newStatusId): Promise<void> => handleStatusChange(newStatusId)}
              />
            </Grid>
          </Grid>
          {!getTenantRequiresApprovals({ tenant: ctx.userInfo.tenant, quotePolicies }) &&
            showCompetitorInfoSection && (
              <LossReasonSection
                title='Why was this quote lost?'
                isLossReasonRequired={requiredFields.lossDetails}
                isCompetitorRequired={requiredFields.lossDetails}
                lossReasons={lossReasons?.items ?? []}
                currencySymbol={competitorCurrencySymbol}
              />
            )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputDropdown name='user.id' label='Salesperson*' options={userLookups} />
            </Grid>
            <Grid item xs={6}>
              <Box display='flex' alignItems='flex-start'>
                <ApiLookupInput
                  formState={formikBag.values}
                  name='project'
                  label='Project*'
                  route={{
                    barrel: 'GET project summaries',
                    args: (inputText, { constructQueryParams }) =>
                      constructQueryParams(inputText, {
                        filterBy: [
                          {
                            name: 'isArchived',
                            operation: Enums.FilterOperation.Equals,
                            values: [false],
                          },
                          {
                            name: 'statusSystemBehavior',
                            operation: Enums.FilterOperation.NotEquals,
                            values: [Enums.ProjectStatusSystemBehavior.Lost],
                          },
                        ],
                      }),
                    transform: (pl): LookupInputOption[] =>
                      lookups({
                        ...pl,
                        label: (p) => p.name,
                      }),
                  }}
                  onMatchChange={(): void => {
                    pendingProjectChangeRef.current = true;
                  }}
                  tip='Selecting a project will update the current products based on those project products.'
                />
                {(isLoadingProject || isLoadingProjectProducts) && <LoadingSpinner size={20} />}
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <LocalLookupInput
                formState={formikBag.values}
                name='quoteConfig'
                label={requiredLabel(quoteConfigLabel, requiredFields.configurationTemplate)}
                disabled={quoteConfigDisabled}
                options={quoteConfigLookups({
                  project,
                  quoteConfigList,
                })}
                tip={
                  quoteConfigDisabled
                    ? undefined
                    : 'Selecting a configuration will update the current additional services and terms sections.'
                }
                onMatchChange={(quoteConfigID): void => {
                  const { newFormikAdditionalProductsByPlants, newFormikTerms, newProductState } =
                    handleProductState({
                      curProductState: curProductsRef.current,
                      formikAdditionalProductsByPlants: formikBag.values.additionalProductsByPlants,
                      formikMainProductsByPlants: formikBag.values.mainProductsByPlants,
                      formikTerms: formikBag.values.terms,
                      formikQuoteConfig: formikBag.values.quoteConfig,
                      project,
                      quoteConfigList,
                      quoteConfigID,
                      isMoneySwitchOn: defaultIsMoneySwitchOn,
                      marginConfiguration,
                      defaultMargins,
                      defaultToSuggestedPrice,
                      fixedPricingByCompany,
                    });

                  SetFormikValue(
                    formikBag,
                    'additionalProductsByPlants',
                    newFormikAdditionalProductsByPlants,
                  );
                  SetFormikValue(formikBag, 'terms', newFormikTerms);

                  curProductsRef.current = newProductState;
                }}
              />
            </Grid>
          </Grid>
        </>
      )}
      {mode === QuoteDrawerMode.Quick && (
        <QuickModeFields
          requiredFields={requiredFields}
          reconcileBuyerPricing={reconcileBuyerPricing}
        />
      )}
    </Box>
  );
};
